// Cliente HTTP mínimo para la API de SIGEPRO Core.
// El access token se mantiene solo en memoria (no en localStorage) para reducir
// la superficie de robo de token vía XSS; el refresh token sí se guarda en
// localStorage para poder recuperar la sesión al recargar la página — en un
// endurecimiento posterior de producción, esto debería moverse a una cookie
// httpOnly + secure gestionada por el backend.

// Por defecto se asume que el backend se sirve desde el MISMO origen que
// este archivo — ver app/__init__.py, que sirve el frontend ya construido
// junto con la API bajo /api. Así el mismo build funciona sin cambios sin
// importar el dominio/puerto donde se despliegue (localhost:5001 en local,
// https://algo.onrender.com en producción, etc.) — nunca hay que
// reconstruirlo ni fijar un dominio a mano. window.__SIGEPRO_API_BASE__
// sigue disponible como override manual para el único caso en que esto no
// aplica: servir el frontend y el backend desde dos orígenes distintos
// (p. ej. iterando con "npm run dev" + un servidor estático aparte en vez
// de recargar la página servida por el propio Flask).
export const API_BASE = (window as any).__SIGEPRO_API_BASE__ || `${window.location.origin}/api`;

let accessToken: string | null = null;
let refreshToken: string | null = localStorage.getItem("sigepro_refresh_token");

export function setTokens(access: string | null, refresh: string | null) {
  accessToken = access;
  refreshToken = refresh;
  if (refresh) localStorage.setItem("sigepro_refresh_token", refresh);
  else localStorage.removeItem("sigepro_refresh_token");
}

export function getRefreshToken() {
  return refreshToken;
}

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(message: string, status: number, code: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function doFetch(path: string, opts: RequestInit = {}, retry = true): Promise<any> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(opts.headers as Record<string, string> | undefined),
  };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });

  if (res.status === 401 && retry && refreshToken) {
    const refreshed = await tryRefresh();
    if (refreshed) return doFetch(path, opts, false);
  }

  let body: any = null;
  const text = await res.text();
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }

  if (!res.ok) {
    const message = (body && body.mensaje) || `Error HTTP ${res.status}`;
    const code = (body && body.error) || "error";
    throw new ApiError(message, res.status, code);
  }
  return body;
}

async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) { setTokens(null, null); return false; }
    const data = await res.json();
    setTokens(data.accessToken, data.refreshToken);
    return true;
  } catch {
    setTokens(null, null);
    return false;
  }
}

async function doFetchForm(path: string, form: FormData, retry = true): Promise<any> {
  // Subida de archivos (multipart/form-data): a propósito NO se fija el header
  // Content-Type — el navegador debe generarlo con el boundary correcto, cosa
  // que no puede hacer si nosotros lo fijamos primero (a diferencia de doFetch,
  // que sí envía JSON con Content-Type explícito).
  const headers: Record<string, string> = {};
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  const res = await fetch(`${API_BASE}${path}`, { method: "POST", headers, body: form });

  if (res.status === 401 && retry && refreshToken) {
    const refreshed = await tryRefresh();
    if (refreshed) return doFetchForm(path, form, false);
  }

  let body: any = null;
  const text = await res.text();
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }
  if (!res.ok) {
    const message = (body && body.mensaje) || `Error HTTP ${res.status}`;
    const code = (body && body.error) || "error";
    throw new ApiError(message, res.status, code);
  }
  return body;
}

async function doFetchBlob(path: string, retry = true): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  const res = await fetch(`${API_BASE}${path}`, { headers });

  if (res.status === 401 && retry && refreshToken) {
    const refreshed = await tryRefresh();
    if (refreshed) return doFetchBlob(path, false);
  }

  if (!res.ok) {
    const text = await res.text();
    let body: any = null;
    try { body = JSON.parse(text); } catch { /* not JSON */ }
    const message = (body && body.mensaje) || `Error HTTP ${res.status}`;
    const code = (body && body.error) || "error";
    throw new ApiError(message, res.status, code);
  }
  return res.blob();
}

export const api = {
  get: (path: string) => doFetch(path, { method: "GET" }),
  post: (path: string, body?: any) => doFetch(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  put: (path: string, body?: any) => doFetch(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  patch: (path: string, body?: any) => doFetch(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  del: (path: string) => doFetch(path, { method: "DELETE" }),
  restoreSession: async (): Promise<boolean> => {
    if (!refreshToken) return false;
    return tryRefresh();
  },
  downloadBlob: (path: string) => doFetchBlob(path),
  postForm: (path: string, form: FormData) => doFetchForm(path, form),
};

// Dispara la descarga de un archivo protegido (requiere el header
// Authorization, por lo que no se puede enlazar con un <a href> plano) —
// usado para los XML de notas y documento equivalente, y para los .txt de
// información exógena.
export async function descargarArchivo(path: string, filename: string) {
  const blob = await api.downloadBlob(path);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
